import {
  IconButton,
  useMediaQuery,
  Stack,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { IconOrLongButton } from "../atoms/Button";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useReducer,
  useRef,
  useMemo,
} from "react";

const TopRightMenuContext = createContext();

const reducer = (state, { action, item }) => {
  if (action == "add") {
    if (!state.by_id[item.id])
      return {
        by_id: { ...state.by_id, [item.id]: item },
        list: [...state.list, item],
      };
    else {
      const index = state.list.indexOf(state.by_id[item.id]);
      state.list[index] = item;
      return {
        by_id: { ...state.by_id, [item.id]: item },
        list: state.list,
      };
    }
  }
  if (action == "remove") {
    const index = state.list.indexOf(state.by_id[item.id]);
    state.list.splice(index, 1);
    delete state.by_id[item.id];
    return {
      by_id: state.by_id,
      list: state.list,
    };
  }
  throw Error("Error not implemented");
};

export const TopRightMenu = ({ children, ...props }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [state, dispatch] = useReducer(reducer, { by_id: {}, list: [] });
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (func) => {
    return (...args) => {
      setAnchorEl(null);
      func && func(...args);
    };
  };

  const register = (item) => {
    if (!item.id) throw Error("Each register call should contains an id");
    dispatch({ action: "add", item });
  };

  const unregister = (id) => {
    dispatch({ action: "remove", item: { id } });
  };

  const where = (item) => {
    if (item.neverShow) return "menu";
    if (item.alwaysShow) return "top";
    return isSmall ? "menu" : "top";
  };

  const nbMenuItem = useMemo(() => {
    return state.list.filter((item) => where(item) == "menu").length;
  }, [state, isSmall]);

  return (
    <TopRightMenuContext.Provider value={{ isSmall, register, unregister }}>
      {children}
      <Stack direction={"row"} alignItems={"center"} spacing={1} {...props}>
        {state.list.map(
          (item, index) =>
            where(item) == "top" &&
            (item.iconButton ? (
              <IconButton
                key={index}
                color={item.selected ? "secondary" : undefined}
                {...item.props}
                endIcon={undefined}
              >
                {item.endIcon}
              </IconButton>
            ) : (
              <IconOrLongButton
                key={index}
                {...item.props}
                endIcon={item.endIcon}
                selected={item.selected}
              >
                {item.children}
              </IconOrLongButton>
            )),
        )}
        {nbMenuItem > 0 && (
          <>
            <IconButton onClick={handleClick}>
              <MenuIcon sx={{ scale: "0.8" }} />
            </IconButton>
            <Menu
              open={open}
              onClose={handleClose()}
              anchorEl={anchorEl}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: "100vh",
                    width: "30ch",
                  },
                },
              }}
            >
              {state.list.map(
                (item, index) =>
                  where(item) == "menu" && (
                    <MenuItem
                      key={index}
                      {...item.props}
                      onClick={handleClose(item.props.onClick)}
                      color={item.selected ? "secondary" : undefined}
                      sx={{
                        backgroundColor: item.selected
                          ? "secondary.main"
                          : undefined,
                        color: item.selected ? "white" : undefined,
                        "&:hover": {
                          backgroundColor: item.selected
                            ? "secondary.main"
                            : undefined,
                        },
                      }}
                    >
                      {item.endIcon && (
                        <ListItemIcon>{item.endIcon}</ListItemIcon>
                      )}
                      <ListItemText>{item.children}</ListItemText>
                      {item.props.title && (
                        <Typography variant="body2">
                          {item.props.title}
                        </Typography>
                      )}
                    </MenuItem>
                  ),
              )}
            </Menu>
          </>
        )}
      </Stack>
    </TopRightMenuContext.Provider>
  );
};

export const TopRightMenuItem = ({
  neverShow,
  alwaysShow,
  iconButton,
  selected,
  children,
  endIcon,
  ...props
}) => {
  const id = useRef(Math.random().toString(36).slice(2));
  const { register, unregister } = useContext(TopRightMenuContext);

  useEffect(() => {
    register({
      id: id.current,
      children,
      neverShow,
      alwaysShow,
      iconButton,
      selected,
      endIcon,
      props,
    });
    const my_id = id.current;
  }, [selected]);

  return <></>;
};
